import React, { useEffect } from 'react';
import cn from 'classnames';

import ActionMenu from 'components/ActionMenu';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import IconButton from 'components/IconButton';
import Modal from 'components/Modal';
import { TextArea } from 'components/form';
import { Col, Row } from 'components/layout';
import { InsightArticleRequest } from 'lib/models/insight';
import { REGEX } from 'lib/utils/regex';
import { CompanyRequest } from 'lib/models/company';

import styles from './EditableCard.module.scss';

interface EditableCardProps {
  data: { title: string; value?: string; url?: string };
  isUrl: boolean;
  title: string;
  index: number;
  onSave: (
    index: number,
    data:
      | InsightArticleRequest
      | CompanyRequest['culture_stats_attributes'][0]
      | CompanyRequest['benefit_stats_attributes'][0]
  ) => void;
  onRemove: (index: number) => void;
  trigger?: React.ReactNode;
  isGrabbing: boolean;
  showControls?: boolean;
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
}

export const EditableCard: React.FC<EditableCardProps> = ({
  data,
  isUrl = false,
  title: modalTitle,
  index,
  onSave,
  onRemove,
  trigger,
  isGrabbing,
  showControls,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [title, setTitle] = React.useState<string>(data.title || '');
  const [subTitle, setSubTitle] = React.useState<string>(data.value || '');
  const [url, setUrl] = React.useState<string>(data.url || '');
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const onClose = () => {
    setIsOpen(false);
    setTitle(data.title || '');
    setSubTitle(data.value || '');
    setUrl(data.url || '');
    setErrors({});
  };

  const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
    setErrors({ ...errors, title: '' });
  };

  const onSubTitleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSubTitle(event.target.value);
  };

  const onUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    setErrors({ ...errors, url: '' });
  };

  const handleSaveClick = () => {
    let isInvalid = false;

    if (!title || (isUrl && (!url || !url.match(REGEX.URL)))) {
      setErrors({
        title: !title ? 'Title is required' : '',
        url:
          isUrl && (!url || !url.match(REGEX.URL)) ? 'Enter a valid Url' : '',
      });
      isInvalid = true;
    }

    if (isInvalid) {
      return;
    }

    const data = { title, ...(isUrl ? { url } : { value: subTitle }) };
    onSave(index, data);
    setIsOpen(false);
  };

  const handleRemoveClick = () => {
    onRemove(index);
  };

  const actionMenu = [
    {
      id: '1',
      label: 'Edit',
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsOpen(true);
      },
    },
    {
      id: '2',
      label: 'Delete',
      danger: true,
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        handleRemoveClick();
      },
    },
  ];

  useEffect(() => {
    if (isGrabbing) {
      document.body.style.cursor = 'grabbing';
    } else {
      document.body.style.cursor = 'default';
    }
  }, [isGrabbing]);

  return (
    <>
      {isOpen ? (
        <Modal
          contentClassName={styles.EditValueModal}
          isOpen={isOpen}
          onRequestClose={onClose}
        >
          <div>
            <IconButton
              size="small"
              iconName="close"
              className={styles.cancelIcon}
              onClick={onClose}
            />
            <div className={styles.header}>{modalTitle}</div>

            <div className={styles.content}>
              <Row>
                <Col span={24} className={styles.contentFieldColumn}>
                  <InputV2
                    label="Title"
                    value={title}
                    onChange={onTitleChange}
                    labelClassName={styles.labelField}
                    isInvalid={!!errors.title}
                    helperText={errors.title}
                  />
                </Col>
                <Col span={24} className={styles.contentFieldColumn}>
                  {isUrl ? (
                    <>
                      <InputV2
                        label="Url"
                        value={url}
                        onChange={onUrlChange}
                        labelClassName={styles.labelField}
                        isInvalid={!!errors.url}
                        helperText={errors.url}
                      />
                    </>
                  ) : (
                    <>
                      <TextArea
                        rows={4}
                        label="Subtitle"
                        onChange={onSubTitleChange}
                        value={subTitle}
                        labelClassName={styles.labelField}
                        textAreaClassName={styles.textArea}
                        isValid
                        showOptionalLabel
                      />
                    </>
                  )}
                </Col>
              </Row>
            </div>
            <div className={styles.footer}>
              <ButtonV3
                type="button"
                className={styles.footerSaveButton}
                onClick={handleSaveClick}
              >
                Done
              </ButtonV3>
            </div>
          </div>
        </Modal>
      ) : (
        <div
          className={cn(styles.card, { [styles.grabbed]: isGrabbing })}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <div
            onClick={() => {
              setIsOpen(true);
            }}
            className={styles.cardContent}
          >
            <div className={styles.cardTitle}>{title}</div>
            {url || subTitle ? (
              <div className={isUrl ? styles.cardLink : styles.cardSubtitle}>
                {isUrl ? url : subTitle}
              </div>
            ) : null}
          </div>

          {showControls ? (
            <div className={styles.actionMenu}>
              <ActionMenu
                options={actionMenu}
                triggerIconClassName={styles.cardTrigger}
                listClassName={styles.options}
                triggerIconSize="xsmallplus"
              />
            </div>
          ) : null}
          {showControls ? (
            <span
              className={cn(styles.dragIcon)}
              style={{ cursor: isGrabbing ? 'grabbing' : 'grab' }}
            >
              {trigger}
            </span>
          ) : null}
        </div>
      )}
    </>
  );
};

EditableCard.displayName = 'MultiEditableCardFieldInput';
